import { API_BASE as BASE_URL, baseHeaders, getAuthHeaders, apiFetch } from './api-fetch';

function mapLeadFromAPI(raw: any) {
  return {
    id: raw.leadId,
    propertyId: raw.propertyId || '',
    propertyType: raw.type || 'buyer',
    leadInfo: {
      name: [raw.firstName, raw.lastName].filter(Boolean).join(' ') || 'Unknown',
      email: raw.email || '',
      phone: raw.phone || '',
      message: raw.notes || '',
    },
    timestamp: raw.createdAt ? new Date(Number(raw.createdAt)).toISOString() : new Date().toISOString(),
    status: raw.status || 'new',
    assignedTo: raw.assignedAgentId || undefined,
    assignedAt: raw.allocatedAt ? new Date(Number(raw.allocatedAt)).toISOString() : undefined,
    source: raw.source || 'manual',
  };
}

export const leadAllocationAPI = {
  // Allocate a lead to an agent (or round-robin if no agentId)
  async allocateLeads(leadIds: string[], agentIds: string[]) {
    const results = [];
    for (let i = 0; i < leadIds.length; i++) {
      try {
        const body: any = {};
        if (agentIds[i]) body.agentId = agentIds[i];
        const response = await apiFetch(`${BASE_URL}/leads/${leadIds[i]}/allocate`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.error || 'Failed to allocate lead');
        }
        results.push(await response.json());
      } catch (error) {
        console.error('Error allocating lead', leadIds[i], error);
        throw error;
      }
    }
    return results;
  },

  // Get ALL leads as admin — single call, returns raw mapped array
  async getAllLeadsAdmin(): Promise<ReturnType<typeof mapLeadFromAPI>[]> {
    try {
      const response = await apiFetch(`${BASE_URL}/leads`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to fetch leads');
      }
      const data = await response.json();
      const leads: any[] = data.data?.leads ?? data.leads ?? [];
      return leads.map(mapLeadFromAPI);
    } catch (error) {
      console.error('Error fetching all leads:', error);
      return [];
    }
  },

  // Get unallocated leads — derived from getAllLeadsAdmin, no separate API call needed
  async getUnallocatedLeads() {
    const all = await leadAllocationAPI.getAllLeadsAdmin();
    return all.filter((l) => !l.assignedTo);
  },

  // Get agent's own leads from CLIENTS_TABLE via GET /agent-leads/:agentId
  async getAgentLeads(agentId: string) {
    if (!agentId) return [];
    try {
      const response = await apiFetch(`${BASE_URL}/agent-leads/${encodeURIComponent(agentId)}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to fetch leads');
      }
      const data = await response.json();
      const leads: any[] = data.data?.leads ?? data.leads ?? [];
      // Backend already returns mapped Lead shape (id, leadInfo, etc.) — don't re-apply mapLeadFromAPI
      return leads;
    } catch (error) {
      console.error('Error fetching agent leads:', error);
      return [];
    }
  },

  // Create a lead manually
  async createLead(payload: {
    firstName: string; lastName: string; email: string; phone: string;
    type: string; source: string; notes?: string; propertyId?: string;
  }) {
    const response = await apiFetch(`${BASE_URL}/leads`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to create lead');
    }
    const data = await response.json();
    const raw = data.data?.lead ?? data.lead;
    return raw ? mapLeadFromAPI(raw) : null;
  },

  // Update a lead (status, notes, etc)
  async updateLead(leadId: string, fields: Record<string, any>) {
    const response = await apiFetch(`${BASE_URL}/leads/${leadId}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to update lead');
    }
    return response.json();
  },

  // Convert a lead to a client
  async convertLead(leadId: string) {
    const response = await apiFetch(`${BASE_URL}/leads/${leadId}/convert`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to convert lead');
    }
    return response.json();
  },
};

export const signingCompletionAPI = {
  // Complete a signing
  async completeSigning(signingId: string, completedBy: string, notes?: string) {
    try {
      const response = await apiFetch(`${BASE_URL}/signings/${signingId}/complete`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ completedBy, notes }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to complete signing');
      }

      return await response.json();
    } catch (error) {
      console.error('Error completing signing:', error);
      throw error;
    }
  },

  // Get signing completion statistics
  async getCompletionStats() {
    try {
      const response = await apiFetch(`${BASE_URL}/signing-completion-stats`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch completion stats');
      }

      const data = await response.json();
      return data.stats || {};
    } catch (error) {
      console.error('Error fetching completion stats:', error);
      return {};
    }
  },
};

export const stockSharingAutoPostAPI = {
  // Auto-post property to stock sharing
  async autoPostToStock(propertyId: string, agentId?: string, agentName?: string) {
    try {
      const response = await apiFetch(`${BASE_URL}/auto-post-to-stock`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ propertyId, agentId, agentName }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to auto-post to stock');
      }

      return await response.json();
    } catch (error) {
      console.error('Error auto-posting to stock:', error);
      throw error;
    }
  },
};

export const analyticsAPI = {
  // Get Year-over-Year and Month-over-Month trends
  async getTrends() {
    try {
      const response = await apiFetch(`${BASE_URL}/admin/analytics/trends`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch analytics trends');
      }

      const data = await response.json();
      return data.analytics || {};
    } catch (error) {
      console.error('Error fetching analytics trends:', error);
      return {
        currentMonth: { deals: 0, revenue: 0, closedDeals: 0 },
        lastMonth: { deals: 0, revenue: 0, closedDeals: 0 },
        lastYearSameMonth: { deals: 0, revenue: 0, closedDeals: 0 },
        trends: {
          mom: { dealsChange: 0, revenueChange: 0 },
          yoy: { dealsChange: 0, revenueChange: 0 }
        },
        monthlyTrends: []
      };
    }
  },
};